'use client';

import React from 'react';
import { format } from 'date-fns';
import { formatDistance } from '@trainme/api/client';
import type { Workout } from '@trainme/db/client';
import { Tooltip } from '@/app/components/ui/Tooltip';
import { useUnitSystem } from '@/app/hooks/useUnitSystem';
import { secondsToHm } from '@/utils/timeUtils';

interface WorkoutTooltipProps {
  workout: Workout;
  children: React.ReactNode;
}

export const WorkoutTooltip = ({ workout, children }: WorkoutTooltipProps) => {
  const unitSystem = useUnitSystem();
  const content = (
    <div className='space-y-0.5 text-xs text-left min-w-[240px] max-w-[320px]'>
      {workout.date && (
        <div className='flex items-center gap-2'>
          <span className='text-slate-500 dark:text-slate-400 font-medium whitespace-nowrap'>
            Date:
          </span>
          <span className='text-slate-700 dark:text-slate-300'>
            {format(new Date(workout.date), 'MMM dd, yyyy (EEEE)')}
          </span>
        </div>
      )}
      {workout.sportType && (
        <div className='flex items-center gap-2'>
          <span className='text-slate-500 dark:text-slate-400 font-medium whitespace-nowrap'>
            Sport:
          </span>
          <span className='text-slate-700 dark:text-slate-300'>
            {workout.sportType}
          </span>
        </div>
      )}
      {(workout.duration || workout.distance) && (
        <div className='flex items-center gap-2'>
          <span className='text-slate-500 dark:text-slate-400 font-medium whitespace-nowrap'>
            Stats:
          </span>
          <span className='text-slate-700 dark:text-slate-300'>
            {workout.duration && secondsToHm((workout.duration || 0) * 60)}
            {workout.duration && workout.distance && ', '}
            {workout.distance &&
              formatDistance((workout.distance || 0) * 1000, unitSystem)}
          </span>
        </div>
      )}
      {workout.description && (
        <div className='pt-1 border-t border-slate-300 dark:border-slate-700 flex items-start gap-2'>
          <span className='text-slate-500 dark:text-slate-400 font-medium whitespace-nowrap'>
            Description:
          </span>
          <span className='text-slate-700 dark:text-slate-300'>
            {workout.description}
          </span>
        </div>
      )}
      {workout.steps &&
        Array.isArray(workout.steps) &&
        workout.steps.length > 0 && (
          <div className='pt-1 border-t border-slate-300 dark:border-slate-700'>
            <div className='flex items-start gap-2'>
              <span className='text-slate-500 dark:text-slate-400 font-medium whitespace-nowrap'>
                Steps:
              </span>
              <div className='text-slate-700 dark:text-slate-300 space-y-0.5 flex-1 max-h-48 overflow-y-auto'>
                {workout.steps.map((step, idx) => (
                  <div
                    key={`step-${workout.name || 'workout'}-${idx}-${String(step).slice(0, 10)}`}
                  >
                    {String(step)}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
    </div>
  );

  return (
    <Tooltip content={content} placement='bottom-start' className='w-full'>
      {children}
    </Tooltip>
  );
};
